import '@fasstech/flexiget/dist/css/main.css';
import '@fasstech/flexiget-mme/dist/css/main.css';
import '@fasstech/flexiget-april/dist/css/main.css';
import React, { useEffect, useState } from 'react';
import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';
import { always, concat, equals, ifElse, prop, propOr, isNotNil, isNotEmpty } from 'ramda';
import useWidget from '../../../../hooks/useWidget';
import withUser from '../../../../withUser';
import QPredefinedVariables from '../../../../_graphql/queries/QPredefinedVariables';
import useFetchQuery from '../../../../hooks/useFetchQuery';
import { QGlobalVariablesQuery } from '../../../../_graphql/queries/documents/QGlobalVariables';
import { getVariablesForRules } from '../../../../utils/getVariablesForRules';
import { PREDEFINED_VARIABLES_FOR_RULES, SCOPE_OFFER } from '../../../../_CONST';
import { ROLES } from '../../../../lib/rights';
import { hasRole } from '../../../../components/users/utils/roles';
import WidgetFormCore from './WidgetFormCore';

const WidgetForm = ({
  widget,
  scope,
  offerId,
  modalMode = false,
  hideModal,
  preview,
  flexigets,
  predefinedVariables,
  setRefetch,
  userTeam,
  totalPaddingX = 0,
  setWidgetCreated,
  onCancel,
  updateWidget,
  userIsAdmin,
  globalVariables,
  setGlobalVariables,
  origin,
  pages,
  documentId,
  documentVariables
}) => {
  const userIsOfferEditor = equals(scope, SCOPE_OFFER) && hasRole(ROLES.offerEditor, propOr([], 'roles', userTeam), offerId);
  const userCanEdit = userIsAdmin || userIsOfferEditor;

  const onOfferRedirect = () => ifElse(
    equals(true),
    () => {
      hideModal && hideModal();
      return `/offre/${offerId}/edition/documents/creer`;
    },
    always(`/offre/${offerId}/edition`)
  )(modalMode);

  const { globalVariables: globalVariablesFetch, reFetch } = useFetchQuery({
    query: QGlobalVariablesQuery,
    args: {
      scope,
      offerId,
      checkUsage: false
    },
    dataProp: 'globalVariables',
    defaultData: [],
    skip: isNotNil(globalVariables)
  });
  const [finalGlobalVariables, setFinalGlobalVariables] = useState(isNotNil(globalVariables) ? globalVariables : []);
  const [variablesForRules, setVariablesForRules] = useState(getVariablesForRules([...globalVariablesFetch, ...PREDEFINED_VARIABLES_FOR_RULES]));

  useEffect(() => {
    if (isNotEmpty(globalVariablesFetch) && !globalVariables) {
      setFinalGlobalVariables(globalVariablesFetch);
    } else if (isNotNil(globalVariables)) {
      setFinalGlobalVariables(globalVariables);
    }
  }, [globalVariablesFetch, globalVariables]);

  const {
    values,
    loading,
    deleteLoading,
    isValid,
    setState,
    onSave,
    isEdit,
    onDelete,
    isUsed,
    isUsedBy,
    widgetExist,
    elements,
    isEditing,
    variablesOnElements,
    getConditionsAreValid,
    updateRule,
    removeRule,
    isOpenCreateGlobalVariableModal,
    setIsOpenCreateGlobalVariableModal,
    selectVariable,
    hasVariableOnRulesNotInVariables
  } = useWidget({
    widget,
    scope,
    offerId,
    onOfferRedirect,
    flexigets,
    setWidgetCreated,
    variablesForRules,
    globalVariables: finalGlobalVariables,
    origin,
    pages,
    documentId
  });

  useEffect(() => {
    const globalVariablesAndPredefinedForRules = getVariablesForRules([...finalGlobalVariables, ...PREDEFINED_VARIABLES_FOR_RULES]);
    const newVariablesForRules = concat(variablesOnElements, globalVariablesAndPredefinedForRules);
    if (!equals(variablesForRules, newVariablesForRules)) {
      setVariablesForRules(newVariablesForRules);
    }
  }, [variablesForRules, finalGlobalVariables, variablesOnElements]);

  const onSaveAndQuit = async () => {
    const res = await onSave(null, !modalMode)();
    if (res && modalMode) {
      if (setRefetch) setRefetch(true);
      const newName = prop('name', values);
      updateWidget && updateWidget(values, newName);
      hideModal && hideModal();
    }
  };

  const setGlobalVariableCreated = (indexFocus) => (variable) => {
    setIsOpenCreateGlobalVariableModal(false);
    const globalVariablesUpdated = concat(finalGlobalVariables, [variable]);
    if (modalMode) {
      setGlobalVariables && setGlobalVariables(globalVariablesUpdated);
    } else {
      reFetch();
    }
    setFinalGlobalVariables(globalVariablesUpdated);
    const conditionIndex = prop('conditionIndex', isOpenCreateGlobalVariableModal);
    selectVariable(indexFocus, conditionIndex, prop('name', variable));
  };

  return (
    <WidgetFormCore
      modalMode={modalMode}
      offerId={offerId}
      values={values}
      preview={preview}
      isEdit={isEdit}
      widgetExist={widgetExist}
      userCanEdit={userCanEdit}
      setState={setState}
      scale={1}
      totalPaddingX={totalPaddingX}
      elements={elements}
      flexigets={flexigets}
      isUsedBy={isUsedBy}
      isUsed={isUsed}
      isEditing={isEditing}
      onDelete={onDelete}
      deleteLoading={deleteLoading}
      onCancel={onCancel}
      onSave={onSave}
      loading={loading}
      hasVariableOnRulesNotInVariables={hasVariableOnRulesNotInVariables}
      isValid={isValid}
      isOpenCreateGlobalVariableModal={isOpenCreateGlobalVariableModal}
      onSaveAndQuit={onSaveAndQuit}
      variablesForRules={variablesForRules}
      updateRule={updateRule}
      removeRule={removeRule}
      getConditionsAreValid={getConditionsAreValid}
      setIsOpenCreateGlobalVariableModal={setIsOpenCreateGlobalVariableModal}
      setGlobalVariableCreated={setGlobalVariableCreated}
      documentVariables={documentVariables}
      predefinedVariables={predefinedVariables}
    />
  );
};

// Stable identity: a fresh `[]` on every render would defeat the memoization the
// variables library relies on (see VariablesLibrary / useVariablesSearch).
const NO_PREDEFINED_VARIABLES = [];

const WidgetFormWrapper = (props) => (
  <QPredefinedVariables>
    {({ predefinedVariables }) => (
      <WidgetForm predefinedVariables={predefinedVariables || NO_PREDEFINED_VARIABLES} {...props} />
    )}
  </QPredefinedVariables>
);
export default withUser(WidgetFormWrapper);
